import { useRef, useState } from "react";
import { API } from "../lib/api";

const GREETING = {
  id: "greeting",
  role: "assistant",
  content:
    "Hello — I'm the HAQILA assistant. Ask me anything about our Indonesian coconut charcoal, MOQ, payment terms, packaging or export process. You can write in any language — I'll reply in yours.",
};

const FALLBACK = "The assistant is temporarily unavailable. Please use the quote form or WhatsApp.";

function getSessionId() {
  let id = localStorage.getItem("haqila_chat_session");
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem("haqila_chat_session", id);
  }
  return id;
}

export function useChatStream() {
  const [messages, setMessages] = useState([GREETING]);
  const [streaming, setStreaming] = useState(false);
  const sessionRef = useRef(getSessionId());

  const updateLast = (updater) =>
    setMessages((prev) => {
      const next = [...prev];
      next[next.length - 1] = updater(next[next.length - 1]);
      return next;
    });

  const handleSseData = (data) => {
    if (data === "[DONE]") return;
    let parsed;
    try {
      parsed = JSON.parse(data);
    } catch {
      console.debug("Chat: skipped partial SSE chunk");
      return;
    }
    if (parsed.delta) {
      const cleanDelta = parsed.delta.replace(/\*\*/g, "");
      updateLast((m) => ({ ...m, content: m.content + cleanDelta }));
    } else if (parsed.error) {
      updateLast((m) => ({ ...m, content: parsed.error }));
    }
  };

  const readStream = async (body) => {
    const reader = body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const events = buffer.split("\n\n");
      buffer = events.pop() || "";
      for (const evt of events) {
        const line = evt.trim();
        if (line.startsWith("data:")) handleSseData(line.slice(5).trim());
      }
    }
  };

  const send = async (message) => {
    setMessages((prev) => [
      ...prev,
      { id: crypto.randomUUID(), role: "user", content: message },
      { id: crypto.randomUUID(), role: "assistant", content: "" },
    ]);
    setStreaming(true);
    try {
      const res = await fetch(`${API}/api/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionRef.current, message }),
      });
      if (!res.ok || !res.body) throw new Error(`chat request failed: ${res.status}`);
      await readStream(res.body);
    } catch (err) {
      console.error("Chat error:", err);
      updateLast((m) => ({ ...m, content: FALLBACK }));
    } finally {
      setStreaming(false);
    }
  };

  return { messages, streaming, send };
}
